import { useTranslation } from 'react-i18next';
import type { ISchedulerTask, SiteId } from '@interfaces/IScheduler';
import { Button } from '@components/ui/Button/Button';
import { isNoopDelta } from '@d-orca/shared/checklist';
import { useChecklistAmendment } from '../../hooks/useChecklistAmendment';
import { checkOrdinals, ordinalLabel } from '../../lib/checklistItems';
import { ChecklistEditor, ChecklistItemSummary } from './ChecklistEditor';
import styles from './ChecklistAuthoring.module.css';

// The Edit tab's checklist — the task's definition of done
// (docs/specs/2026-09-28-task-checklist-design.md).
//
// Before the commit it is plan like any other Edit-tab field: edited on the
// drawer DRAFT and saved with it. The commit locks it (decision 5): the list
// turns read-only, and changing it is an explicit AMENDMENT — saved at once
// through its own route, logged on the task and audited — never a quiet edit
// riding the next Save.

type ChecklistPatch = Pick<ISchedulerTask, 'checklist' | 'checklistTemplateName'>;

interface IChecklistAuthoringProps {
  /** The task as its checklist is shown: the loaded one once committed (the
   *  commit and every amendment land there), else the drawer draft. */
  task: ISchedulerTask;
  siteId: SiteId;
  scheduleNumber?: string;
  /** Write the checklist into the drawer draft. */
  onChange: (patch: ChecklistPatch) => void;
}

export function ChecklistAuthoring({ task, siteId, scheduleNumber, onChange }: IChecklistAuthoringProps) {
  const { t } = useTranslation();
  return (
    <section className={styles.section}>
      <h3 className={styles.title}>{t('scheduler.panel.checklist.title')}</h3>
      <p className={styles.subtitle}>{t('scheduler.panel.checklist.subtitle')}</p>
      {task.committed ? (
        <CommittedChecklist task={task} siteId={siteId} scheduleNumber={scheduleNumber} onChange={onChange} />
      ) : (
        <ChecklistEditor
          siteId={siteId}
          items={task.checklist ?? []}
          templateName={task.checklistTemplateName}
          onChange={(checklist, checklistTemplateName) => onChange({ checklist, checklistTemplateName })}
        />
      )}
    </section>
  );
}

function CommittedChecklist({ task, siteId, scheduleNumber, onChange }: IChecklistAuthoringProps) {
  const { t } = useTranslation();
  const { committed, draft, start } = useChecklistAmendment(siteId, task, (checklist) => onChange({ checklist }));
  const ordinals = checkOrdinals(committed);

  if (draft) {
    return (
      <>
        <ChecklistEditor siteId={siteId} items={draft.items} templateName={task.checklistTemplateName} onChange={draft.edit} />
        {draft.failed && <p className={styles.error} role="alert">{t('scheduler.panel.checklist.amendFailed')}</p>}
        <div className={styles.editorFooter}>
          <p className={styles.footerNote} data-testid="checklist-amend-summary">
            {t(isNoopDelta(draft.delta) ? 'scheduler.panel.checklist.amendNoChange' : 'scheduler.panel.checklist.amendSummary', { ...draft.delta })}
          </p>
          <Button variant="ghost" size="sm" onClick={draft.cancel}>{t('scheduler.panel.checklist.editorCancel')}</Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={draft.pending || isNoopDelta(draft.delta)}
            onClick={() => void draft.save()}
            data-testid="checklist-amend-save"
          >
            {t('scheduler.panel.checklist.amendSave')}
          </Button>
        </div>
      </>
    );
  }

  return (
    <>
      <ol className={styles.list}>
        {committed.map((item) => (
          <li key={item.id} className={`${styles.item} ${styles.itemLocked}`}>
            <span className={styles.ordinal} aria-hidden>{ordinalLabel(ordinals.get(item.id))}</span>
            <ChecklistItemSummary item={item} />
          </li>
        ))}
      </ol>
      <p className={styles.provenance}>
        {t(task.status === 'done' ? 'scheduler.panel.checklist.lockedDone' : 'scheduler.panel.checklist.locked')}
      </p>
      {/* Amending writes straight to the loaded schedule, so it needs one. */}
      {task.status !== 'done' && scheduleNumber && (
        <Button variant="secondary" size="sm" className={styles.amendBtn} onClick={() => start(scheduleNumber)} data-testid="checklist-amend">
          {t('scheduler.panel.checklist.amend')}
        </Button>
      )}
    </>
  );
}
